/**
 * Planning user-assignment APIs (task / location assignees).
 *
 * GET/POST  dpr/apis/planning/user-assignment
 * GET       dpr/apis/planning/task/{taskId}/user-assignment  (task-scoped; unused by UI for now)
 * PUT       dpr/apis/planning/user-assignment/{assignmentId}/status
 * DELETE    dpr/apis/planning/user-assignment/{assignmentId}
 */
import { notifications } from '@mantine/notifications';
import { showErrorNotification } from '../utils/utils';
import { transactionAxios } from './schedulingApis';

const successNotificationColor = 'teal';

export const USER_ASSIGNMENT_SCOPE_TYPES = ['location', 'task'] as const;
export type UserAssignmentScopeType = (typeof USER_ASSIGNMENT_SCOPE_TYPES)[number];

export const USER_ASSIGNMENT_ROLES = ['planner', 'capturer', 'reviewer'] as const;
export type UserAssignmentRole = (typeof USER_ASSIGNMENT_ROLES)[number];

export const USER_ASSIGNMENT_STATUSES = ['active', 'inactive'] as const;
export type UserAssignmentStatus = (typeof USER_ASSIGNMENT_STATUSES)[number];

export interface PlanningUserAssignment {
  id: string;
  projectId: string;
  scopeType: UserAssignmentScopeType | string;
  locationId: string | null;
  taskId: string | null;
  userId: string;
  userName: string | null;
  role: UserAssignmentRole | string;
  status: UserAssignmentStatus | string;
  notes?: string | null;
  createdBy?: string | null;
  createdAt?: string | null;
  updatedBy?: string | null;
  updatedAt?: string | null;
  meta?: Record<string, unknown> | null;
}

export interface FetchUserAssignmentsParams {
  projectId: string;
  scopeType?: UserAssignmentScopeType | string;
  status?: UserAssignmentStatus | string;
}

export interface FetchTaskUserAssignmentsParams {
  taskId: string;
  status?: UserAssignmentStatus | string;
}

export interface AddUserAssignmentRequest {
  /** Client-generated assignment id (required by API). */
  id: string;
  projectId: string;
  scopeType: UserAssignmentScopeType | string;
  locationId: string;
  /** Required when scopeType is `task`; omit for `location`. */
  taskId?: string | null;
  userId: string;
  role: UserAssignmentRole | string;
  notes?: string | null;
  meta?: Record<string, unknown> | null;
}

export interface UpdateUserAssignmentStatusRequest {
  assignmentId: string;
  status: UserAssignmentStatus | string;
}

function asArray(value: unknown): unknown[] {
  if (Array.isArray(value)) return value;
  if (value != null && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    for (const key of ['data', 'payload', 'result']) {
      if (Array.isArray(record[key])) return record[key] as unknown[];
    }
  }
  return [];
}

function unwrapEnvelope(raw: unknown): unknown {
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) return raw;
  const record = raw as Record<string, unknown>;
  if (record.data != null) return record.data;
  if (record.payload != null) return record.payload;
  return raw;
}

export function parsePlanningUserAssignment(raw: unknown): PlanningUserAssignment | null {
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const row = raw as Record<string, unknown>;
  const id = String(row.id ?? '').trim();
  const projectId = String(row.projectId ?? row.project_id ?? '').trim();
  const userId = String(row.userId ?? row.user_id ?? '').trim();
  if (!id || !projectId || !userId) return null;

  const locationRaw = row.locationId ?? row.location_id;
  const taskRaw = row.taskId ?? row.task_id;
  const notesRaw = row.notes;
  const metaRaw = row.meta;

  return {
    id,
    projectId,
    scopeType: String(row.scopeType ?? row.scope_type ?? '').trim() || 'task',
    locationId:
      locationRaw == null || String(locationRaw).trim() === ''
        ? null
        : String(locationRaw).trim(),
    taskId:
      taskRaw == null || String(taskRaw).trim() === '' ? null : String(taskRaw).trim(),
    userId,
    userName:
      row.userName != null || row.user_name != null
        ? String(row.userName ?? row.user_name).trim() || null
        : null,
    role: String(row.role ?? '').trim() || 'planner',
    status: String(row.status ?? '').trim() || 'active',
    notes:
      notesRaw == null || String(notesRaw).trim() === ''
        ? null
        : String(notesRaw),
    createdBy:
      row.createdBy != null || row.created_by != null
        ? String(row.createdBy ?? row.created_by).trim() || null
        : null,
    createdAt:
      row.createdAt != null || row.created_at != null
        ? String(row.createdAt ?? row.created_at)
        : null,
    updatedBy:
      row.updatedBy != null || row.updated_by != null
        ? String(row.updatedBy ?? row.updated_by).trim() || null
        : null,
    updatedAt:
      row.updatedAt != null || row.updated_at != null
        ? String(row.updatedAt ?? row.updated_at)
        : null,
    meta:
      metaRaw != null && typeof metaRaw === 'object' && !Array.isArray(metaRaw)
        ? (metaRaw as Record<string, unknown>)
        : null,
  };
}

export function parsePlanningUserAssignments(raw: unknown): PlanningUserAssignment[] {
  return asArray(unwrapEnvelope(raw))
    .map(parsePlanningUserAssignment)
    .filter((row): row is PlanningUserAssignment => row != null);
}

/** GET dpr/apis/planning/user-assignment */
export async function fetchUserAssignments(
  params: FetchUserAssignmentsParams,
  options?: { silent?: boolean },
): Promise<PlanningUserAssignment[]> {
  const projectId = String(params.projectId ?? '').trim();
  if (!projectId) {
    throw new Error('projectId is required to fetch user assignments.');
  }

  const query: Record<string, string> = { projectId };
  if (params.scopeType != null && String(params.scopeType).trim() !== '') {
    query.scopeType = String(params.scopeType).trim();
  }
  if (params.status != null && String(params.status).trim() !== '') {
    query.status = String(params.status).trim();
  }

  try {
    const res = await transactionAxios.get('dpr/apis/planning/user-assignment', {
      params: query,
    });
    return parsePlanningUserAssignments(res.data);
  } catch (err) {
    console.error(`Error fetching user assignments: ${err}`);
    if (!options?.silent) {
      showErrorNotification(err, `Sorry! Failed to fetch user assignments.`);
    }
    throw err;
  }
}

/**
 * GET dpr/apis/planning/task/{taskId}/user-assignment
 *
 * Task-scoped fetch for when project-wide assignment lists become too heavy.
 * Not wired into Gantt yet — keep using {@link fetchUserAssignments} + client filter.
 */
export async function fetchTaskUserAssignments(
  params: FetchTaskUserAssignmentsParams,
  options?: { silent?: boolean },
): Promise<PlanningUserAssignment[]> {
  const taskId = String(params.taskId ?? '').trim();
  if (!taskId) {
    throw new Error('taskId is required to fetch task user assignments.');
  }

  const query: Record<string, string> = {};
  if (params.status != null && String(params.status).trim() !== '') {
    query.status = String(params.status).trim();
  }

  try {
    const res = await transactionAxios.get(
      `dpr/apis/planning/task/${encodeURIComponent(taskId)}/user-assignment`,
      { params: Object.keys(query).length > 0 ? query : undefined },
    );
    return parsePlanningUserAssignments(res.data);
  } catch (err) {
    console.error(`Error fetching task user assignments: ${err}`);
    if (!options?.silent) {
      showErrorNotification(err, `Sorry! Failed to fetch task user assignments.`);
    }
    throw err;
  }
}

/** POST dpr/apis/planning/user-assignment */
export async function addUserAssignment(
  request: AddUserAssignmentRequest,
  options?: { silent?: boolean },
): Promise<unknown> {
  const id = String(request.id ?? '').trim();
  const projectId = String(request.projectId ?? '').trim();
  const scopeType = String(request.scopeType ?? '').trim();
  const locationId = String(request.locationId ?? '').trim();
  const userId = String(request.userId ?? '').trim();
  const role = String(request.role ?? '').trim();

  if (!id || !projectId || !scopeType || !locationId || !userId || !role) {
    throw new Error(
      'id, projectId, scopeType, locationId, userId, and role are required to add a user assignment.',
    );
  }

  const body: Record<string, unknown> = {
    id,
    projectId,
    scopeType,
    locationId,
    userId,
    role,
  };

  if (scopeType !== 'location') {
    const taskId = request.taskId != null ? String(request.taskId).trim() : '';
    if (taskId) body.taskId = taskId;
  }
  if (request.notes != null && String(request.notes).trim() !== '') {
    body.notes = String(request.notes);
  }
  if (request.meta != null && typeof request.meta === 'object') {
    body.meta = request.meta;
  }

  try {
    const res = await transactionAxios.post('dpr/apis/planning/user-assignment', body, {
      headers: { 'Content-Type': 'application/json' },
    });
    if (!options?.silent) {
      notifications.show({
        title: 'Assignment added',
        message: 'User assignment saved successfully.',
        color: successNotificationColor,
      });
    }
    return res.data;
  } catch (err) {
    console.error(`Error adding user assignment: ${err}`);
    if (!options?.silent) {
      showErrorNotification(err, `Sorry! Failed to add user assignment.`);
    }
    throw err;
  }
}

/**
 * PUT dpr/apis/planning/user-assignment/{assignmentId}/status
 * Body: { status: "active" | "inactive" }
 */
export async function updateUserAssignmentStatus(
  request: UpdateUserAssignmentStatusRequest,
  options?: { silent?: boolean },
): Promise<unknown> {
  const assignmentId = String(request.assignmentId ?? '').trim();
  const status = String(request.status ?? '').trim();
  if (!assignmentId || !status) {
    throw new Error('assignmentId and status are required to update user assignment status.');
  }

  try {
    const res = await transactionAxios.put(
      `dpr/apis/planning/user-assignment/${encodeURIComponent(assignmentId)}/status`,
      { status },
      { headers: { 'Content-Type': 'application/json' } },
    );
    if (!options?.silent) {
      notifications.show({
        title: 'Assignment updated',
        message: `User assignment marked ${status}.`,
        color: successNotificationColor,
      });
    }
    return res.data;
  } catch (err) {
    console.error(`Error updating user assignment status: ${err}`);
    if (!options?.silent) {
      showErrorNotification(err, `Sorry! Failed to update user assignment status.`);
    }
    throw err;
  }
}

/**
 * DELETE dpr/apis/planning/user-assignment/{assignmentId}
 */
export async function deleteUserAssignment(
  assignmentId: string,
  options?: { silent?: boolean },
): Promise<unknown> {
  const id = String(assignmentId ?? '').trim();
  if (!id) {
    throw new Error('assignmentId is required to delete a user assignment.');
  }

  try {
    const res = await transactionAxios.delete(
      `dpr/apis/planning/user-assignment/${encodeURIComponent(id)}`,
    );
    if (!options?.silent) {
      notifications.show({
        title: 'Assignment deleted',
        message: 'User assignment removed successfully.',
        color: successNotificationColor,
      });
    }
    return res.data;
  } catch (err) {
    console.error(`Error deleting user assignment: ${err}`);
    if (!options?.silent) {
      showErrorNotification(err, `Sorry! Failed to delete user assignment.`);
    }
    throw err;
  }
}
